import { notification } from "antd";
import { GlobalStoreContextType } from "../../context/GlobalStoreContext";
import { Area } from "../../models/Area";
import { Equipment } from "../../models/Equipment";
import { MapObject } from "../../models/MapObject";
import { geometry } from "../../models/MapObject.model";
import { MapObjectRadius } from "../../models/MapObjectRadius";
import { WaypointDirection } from "../../models/WaypointDirection";
import PlotAPI from "../../plotAPI";
import {
    createWaypointDirection,
    destroyWaypointDirection,
    updateWaypointDirection,
} from "../PlotAPI/WaypointDirection";

const handleSaveWaypointDirection = async (mappable: Area | Equipment, project: any) => {
    if (!mappable || !(mappable instanceof Area)) {
        return Promise.resolve();
    }
    const projectToken = project.unique_token;
    const teamSubdomain = project.claimed_by_team_subdomain || "projects";

    if (!mappable.waypointDirection?.id && mappable.waypointDirection?._destroy) {
        mappable.waypointDirection = undefined;
    }

    if (!mappable.waypointDirection) {
        return Promise.resolve();
    }

    const tempWaypoint = Object.create(mappable.waypointDirection) as WaypointDirection;
    tempWaypoint.areaId = mappable.id;

    if (tempWaypoint._destroy) {
        return destroyWaypointDirection(tempWaypoint, teamSubdomain, projectToken);
    }
    if (!tempWaypoint.id) {
        return createWaypointDirection(tempWaypoint, teamSubdomain, projectToken);
    }
    return updateWaypointDirection(tempWaypoint, teamSubdomain, projectToken);
};

const handleUpdateMappable = async (
    mappble: Area | Equipment,
    context: GlobalStoreContextType,
    taggedTeams: number[]
) => {
    if (mappble instanceof Area) {
        return PlotAPI.updateArea(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappble,
            taggedTeams,
            context.currentProjectInfo.timezone
        ).catch((error) => {
            notification.error({
                message: "Unable to update Area",
                description: error.message || "",
            });
        });
    }
    if (mappble instanceof Equipment) {
        return PlotAPI.updateEquipment(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappble,
            taggedTeams,
            context.currentProjectInfo.timezone
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to update Equipment",
                    description: "",
                });
            }
        });
    }
    return;
};

const handleUpdateMapObject = async (newMapObject: MapObject, context: GlobalStoreContextType) => {
    const newCurrentMapObject = Object.create(newMapObject) as MapObject;
    const originalMapObject = context
        .mapObjectsForProject(context.currentProjectInfo.unique_token)
        .find((object) => object.id === newCurrentMapObject.id);

    if (!originalMapObject) {
        notification.error({
            message: "Unable to find Map Object",
            description: "The Map Object you are trying to update does not exist.",
        });
        return;
    }

    for (const oldRadius of originalMapObject.radiuses || []) {
        if (!newCurrentMapObject.radiuses?.find((e) => e.id === oldRadius.id)) {
            if (!newCurrentMapObject.radiuses) {
                newCurrentMapObject.radiuses = [];
            }

            const newRadius = MapObjectRadius.fromApiObject(
                {
                    id: oldRadius.id,
                },
                context.currentProjectInfo.timezone
            );

            newRadius._destroy = true; // Mark this for destruction

            newCurrentMapObject.radiuses.push(newRadius);
        }
    }
    return PlotAPI.updateMapObject(
        context.currentProjectInfo.claimed_by_team_subdomain || "projects",
        context.currentProjectInfo.unique_token,
        newCurrentMapObject,
        context.currentProjectInfo.timezone
    )
        .then((res) => {
            if (!res) {
                throw new Error("Unable to update Map Object");
            }
            return res;
        })
        .catch((error) => {
            notification.error({
                message: "Unable to update Map Object",
                description: "",
            });

            return;
        });
};

export const updateMappable = async (
    currentMappable: Area | Equipment,
    currentMapObject: MapObject,
    context: GlobalStoreContextType,
    taggedTeams: number[]
) => {
    let newCurrentMappable =
        currentMappable instanceof Area
            ? (Object.create(currentMappable) as Area)
            : (Object.create(currentMappable) as Equipment);

    const originalMappable =
        currentMappable instanceof Area
            ? context
                  .mapAreasForProject(context.currentProjectInfo.unique_token)
                  .find((area) => area.id === newCurrentMappable.id)
            : context
                  .equipmentForProject(context.currentProjectInfo.unique_token)
                  .find((equipment) => equipment.id === newCurrentMappable.id);

    if (!originalMappable) {
        notification.error({
            message: currentMappable instanceof Area ? "Unable to find Area" : "Unable to find Equipment",
            description:
                currentMappable instanceof Area
                    ? "The Area you are trying to update does not exist."
                    : "The Equipment you are trying to update does not exist.",
        });
        return;
    }

    if (newCurrentMappable instanceof Area && !newCurrentMappable.isWaypoint) {
        newCurrentMappable.isWaypoint = false; // Default to false if not set
    }

    return Promise.all([
        handleUpdateMappable(newCurrentMappable, context, taggedTeams),
        handleUpdateMapObject(currentMapObject, context),
        handleSaveWaypointDirection(newCurrentMappable, context.currentProjectInfo),
    ]);
};

export const createMappable = async (
    currentMappable: Area | Equipment,
    currentMapObject: MapObject,
    context: GlobalStoreContextType,
    taggedTeams: number[]
): Promise<Area | Equipment | null> => {
    if (currentMappable instanceof Area) {
        if (!currentMappable.isWaypoint) {
            currentMappable.isWaypoint = false; // Default to false if not set
        }
        return PlotAPI.createArea(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            currentMappable,
            currentMapObject,
            taggedTeams,
            context.currentProjectInfo.timezone
        )
            .then((res) => {
                if (!res) {
                    throw new Error("Unable to create Area");
                }

                const area = res as Area;

                if (area && currentMappable.isWaypoint && currentMappable.waypointDirection) {
                    const tempWaypoint = Object.create(currentMappable.waypointDirection) as WaypointDirection;
                    tempWaypoint.areaId = area.id;

                    return createWaypointDirection(
                        tempWaypoint,
                        context.currentProjectInfo.claimed_by_team_subdomain,
                        context.currentProjectInfo.unique_token
                    ).then((response) => {
                        return area;
                    });
                }
                return area;
            })
            .catch((error) => {
                notification.error({
                    message: "Unable to create Area",
                    description: error.message || "",
                });

                return null;
            });
    }
    if (currentMappable instanceof Equipment) {
        return PlotAPI.createEquipment(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            currentMappable,
            currentMapObject,
            taggedTeams,
            context.currentProjectInfo.timezone
        )
            .then((data) => {
                if (!data) {
                    throw new Error("Unable to create Equipment");
                }
                return data;
            })
            .catch((error) => {
                notification.error({
                    message: "Unable to create Equipment",
                    description: error.message || "",
                });

                return null;
            });
    }

    return Promise.resolve(null);
};

export const archiveMappable = async (mappable: Area | Equipment, context: GlobalStoreContextType) => {
    if (mappable instanceof Area) {
        return PlotAPI.deleteArea(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappable
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to archive Area",
                    description: `Please check if an area is already archived with the name ${mappable.areaName} (archived)`,
                });
            }
        });
    } else {
        return PlotAPI.deleteEquipment(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappable
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to archive Equipment",
                    description: `Please check if an area is already archived with the name ${mappable.equipmentName} (archived)`,
                });
            }
        });
    }
};

export const objectType = (objectType: geometry): string => {
    if (objectType === "lineString") {
        return "Line";
    } else if (objectType === "polygon") {
        return "Area";
    } else {
        return "Point";
    }
};

export const checkRequiredValues = (
    mappable: Area | Equipment,
    mapObject: MapObject,
    context: GlobalStoreContextType
) => {
    const name = mappable instanceof Area ? mappable.areaName : mappable.equipmentName;

    if (!name || name.length == 0) {
        notification.error({
            message: "Cannot Save Map Location",
            description: "A name is required for your " + objectType(mapObject.geometry).toLowerCase(),
        });

        return false;
    }

    if (["New Area", "New Line", "New Equipment", "New Point"].includes(name)) {
        notification.error({
            message: "Cannot Save Map Location",
            description:
                "Please change your " + objectType(mapObject.geometry).toLowerCase() + " from the default name",
        });

        return false;
    }

    // Check if new waypoint attachments exceed 100MB
    if (mappable instanceof Area && mappable.isWaypoint && mappable.waypointDirection) {
        const newStepAttachments = mappable.waypointDirection.waypointSteps.filter((step) => {
            return step.picture instanceof Blob || step.picture instanceof File;
        });

        const totalSize = newStepAttachments.reduce((sum, step) => sum + (step.picture?.size || 0), 0);
        const maxSize = 100 * 1024 * 1024; // 100MB in bytes

        if (totalSize > maxSize) {
            notification.error({
                message: "Maximum Upload Size Exceeded",
                description:
                    "Total size of new waypoint step attachments exceeds 100MB. Please remove some attachments, reduce image sizes, or upload attachments individually.",
            });
            return false;
        }
    }

    let mappableName = mappable instanceof Area ? mappable.areaName : mappable.equipmentName;
    if (!mappableName) {
        notification.error({
            message: "Cannot Save Map Location",
            description: "A name is required for your " + objectType(mapObject.geometry).toLowerCase(),
        });
        return false;
    }
    mappableName = mappableName.toLowerCase().replaceAll(" ", "");
    const allMappables = [
        ...context.mapAreasForProject(context.currentProjectInfo.id),
        ...context.equipmentForProject(context.currentProjectInfo.id),
    ];

    const duplicateName = allMappables
        .filter((item) => item.id != mappable.id)
        .some((map) => {
            const name = map instanceof Area ? map.areaName : map.equipmentName;
            return name?.toLowerCase().replaceAll(" ", "") == mappableName;
        });

    if (duplicateName) {
        notification.error({
            message: "Cannot Save Map Location",
            description: "A location with this name already exists",
        });
        return false;
    }

    return true;
};
